import { useState, useRef, useCallback } from "react";
import {
  AtMentionSearchTab,
  DEFAULT_AT_MENTION_SEARCH_TAB,
  Suggestion,
  SuggestionSearchResult,
  TriggerConfig,
} from "../types";
import { detectTrigger } from "./useTriggerDetection";
import { EditorHandle } from "../components/Editor";

const MENTION_SEARCH_PAGE_SIZE = 20;

export type UseMentionsOptions = {
  /** Current @-mention tab; drives which search `type` is requested. */
  atMentionTab?: AtMentionSearchTab;
};

const normalizeSuggestionResult = (
  result: Suggestion[] | SuggestionSearchResult
): SuggestionSearchResult => {
  if (Array.isArray(result)) {
    return { items: result, hasMore: false };
  }

  return result;
};

export function useMentions(
  triggers: TriggerConfig[],
  editorRef: React.RefObject<EditorHandle>,
  options?: UseMentionsOptions
) {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTrigger, setActiveTrigger] = useState<string | null>(null);
  const [activeQuery, setActiveQuery] = useState<string>("");
  const [coords, setCoords] = useState<{ top: number; left: number; height: number } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const loadMoreRequestIdRef = useRef(0);
  const currentSearchRef = useRef<{
    triggerChar: string;
    query: string;
    tab: AtMentionSearchTab;
    page: number;
  } | null>(null);

  const reset = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    requestIdRef.current += 1;
    loadMoreRequestIdRef.current += 1;
    currentSearchRef.current = null;
    setItems([]);
    setActiveIndex(0);
    setActiveTrigger(null);
    setActiveQuery("");
    setCoords(null);
    setHasMore(false);
    setIsLoading(false);
    setIsLoadingMore(false);
  }, []);

  const onDismiss = reset;

  const fetchSuggestions = useCallback(
    async ({
      triggerChar,
      query,
      tab,
      nextPage,
      append,
    }: {
      triggerChar: string;
      query: string;
      tab: AtMentionSearchTab;
      nextPage: number;
      append: boolean;
    }) => {
      const config = triggers.find((t) => t.char === triggerChar);
      if (!config) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      let loadMoreRequestId: number | null = null;

      if (append) {
        loadMoreRequestId = loadMoreRequestIdRef.current + 1;
        loadMoreRequestIdRef.current = loadMoreRequestId;
        setIsLoadingMore(true);
      } else {
        loadMoreRequestIdRef.current += 1;
        currentSearchRef.current = null;
        setIsLoading(true);
        setIsLoadingMore(false);
        setHasMore(false);
      }

      try {
        const result = normalizeSuggestionResult(
          await config.getSuggestions(query, {
            tab,
            page: nextPage,
            limit: MENTION_SEARCH_PAGE_SIZE,
          })
        );

        if (requestId !== requestIdRef.current) return;

        currentSearchRef.current = {
          triggerChar,
          query,
          tab,
          page: nextPage,
        };
        setItems((prev) => (append ? [...prev, ...result.items] : result.items));
        setActiveIndex(0);
        setHasMore(Boolean(result.hasMore));
      } finally {
        if (append) {
          if (loadMoreRequestId === loadMoreRequestIdRef.current) {
            setIsLoadingMore(false);
          }
        } else {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      }
    },
    [triggers]
  );

  const handleInput = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const text = editorRef.current?.getText() || "";
    const cursor = editorRef.current?.getCursorIndex() || 0;

    const triggerMatch = detectTrigger(
      text,
      cursor,
      triggers.map((t) => t.char)
    );

    if (!triggerMatch) {
      reset();
      return;
    }

    const triggerChar = triggerMatch.trigger;
    const query = triggerMatch.query;

    requestIdRef.current += 1;
    setActiveTrigger(triggerChar);
    setActiveQuery(query);

    const newCoords = editorRef.current?.getCaretCoordinates();
    if (newCoords) {
      setCoords(newCoords);
    }

    debounceTimerRef.current = setTimeout(async () => {
      const tab: AtMentionSearchTab = options?.atMentionTab ?? DEFAULT_AT_MENTION_SEARCH_TAB;
      fetchSuggestions({
        triggerChar,
        query,
        tab,
        nextPage: 0,
        append: false,
      });
    }, 300);
  }, [triggers, editorRef, reset, options?.atMentionTab, fetchSuggestions]);

  const refetchAtMentionSuggestions = useCallback(
    async (tabOverride?: AtMentionSearchTab) => {
      if (activeTrigger !== "@") return;
      const tab = tabOverride ?? options?.atMentionTab ?? DEFAULT_AT_MENTION_SEARCH_TAB;
      fetchSuggestions({
        triggerChar: "@",
        query: activeQuery,
        tab,
        nextPage: 0,
        append: false,
      });
    },
    [activeTrigger, activeQuery, options?.atMentionTab, fetchSuggestions]
  );

  const loadMoreAtMentionSuggestions = useCallback(
    async () => {
      const currentSearch = currentSearchRef.current;
      if (
        !currentSearch ||
        currentSearch.triggerChar !== "@" ||
        !hasMore ||
        isLoading ||
        isLoadingMore
      ) {
        return;
      }

      fetchSuggestions({
        triggerChar: currentSearch.triggerChar,
        query: currentSearch.query,
        tab: currentSearch.tab,
        nextPage: currentSearch.page + 1,
        append: true,
      });
    },
    [fetchSuggestions, hasMore, isLoading, isLoadingMore]
  );

  const handleSelect = useCallback(
    (item: Suggestion) => {
      const config = triggers.find((t) => t.char === activeTrigger);
      if (!config) return;

      const replaceLength = activeTrigger ? activeTrigger.length + activeQuery.length : 0;
      const node = config.createMentionNode(item);

      editorRef.current?.insertNode(node, replaceLength);

      reset();
    },
    [activeTrigger, activeQuery, editorRef, reset, triggers]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!activeTrigger || items.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleSelect(items[activeIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        reset();
      }
    },
    [activeTrigger, items, activeIndex, handleSelect, reset]
  );

  return {
    items,
    activeIndex,
    coords,
    activeTrigger,
    hasMore,
    handleInput,
    handleKeyDown,
    handleSelect,
    isLoading,
    isLoadingMore,
    loadMoreAtMentionSuggestions,
    reset,
    onDismiss,
    refetchAtMentionSuggestions,
  };
}
